import type { FieldValueType, FileType } from "../../../types";

import type {
  FileFieldConstraints,
  FileInfo,
  UploadedFormDataFileInfo,
} from "./types";

export const isValidUploadedFileInfo = (
  value: unknown,
): value is UploadedFormDataFileInfo => {
  if (value === null || typeof value !== "object") return false;

  const obj = value as Record<string, unknown>;

  return (
    typeof obj.s3Url === "string" &&
    obj.s3Url.length > 0 &&
    typeof obj.mimetype === "string" &&
    obj.mimetype.length > 0 &&
    typeof obj.size === "number" &&
    obj.size >= 0 &&
    typeof obj.originalname === "string" &&
    obj.originalname.length > 0
  );
};

export const convertFileValueToFileInfo = (
  value: FieldValueType,
): readonly FileInfo[] => {
  if (!value) return [];

  const normalizeFileInfo = (item: unknown, index: number): FileInfo => {
    if (item instanceof File) {
      return {
        type: "local",
        file: item,
        preview: item.type.startsWith("image/")
          ? URL.createObjectURL(item)
          : undefined,
        id: `local-file-${item.name}-${index}`,
      };
    }
    if (isValidUploadedFileInfo(item)) {
      return {
        type: "uploaded",
        fileInfo: item,
        id: `uploaded-file-${item.s3Url}-${index}`,
      };
    }

    // 基本上記のどちらかに該当するはずなのでここには来ない想定
    throw new Error("Invalid file item");
  };

  let convertedValue: Record<string, unknown>;
  try {
    convertedValue = (
      typeof value === "string" ? JSON.parse(value) : value
    ) as Record<string, unknown>;
  } catch {
    // JSON.parse に失敗した場合は、value が JSON 形式ではないため、[] を返す
    return [];
  }

  return Array.isArray(convertedValue)
    ? convertedValue.map(normalizeFileInfo)
    : [normalizeFileInfo(convertedValue, 0)];
};

export const extractFileConstraints = (
  fileConfig: unknown,
): FileFieldConstraints => {
  const defaults: FileFieldConstraints = {
    multiple: true,
    maxFiles: 10,
    maxSize: 10 * 1024 * 1024, // 10MB
    acceptedExtensions: [],
    acceptedMimeTypes: [],
  };

  if (
    typeof fileConfig !== "object" ||
    fileConfig === null ||
    !("length" in fileConfig)
  ) {
    return defaults;
  }

  const config = fileConfig as NonNullable<FileType>;
  return {
    multiple: (config.length ?? 1) > 1,
    maxFiles: config.length ?? config.maxLength ?? defaults.maxFiles,
    maxSize: config.maxFileSize ?? defaults.maxSize,
    acceptedExtensions: Array.isArray(config.acceptedExtensions)
      ? config.acceptedExtensions
      : defaults.acceptedExtensions,
    acceptedMimeTypes: Array.isArray(config.acceptedMimeTypes)
      ? config.acceptedMimeTypes
      : defaults.acceptedMimeTypes,
  };
};

export const convertToDropZoneItems = (
  fileInfos: readonly FileInfo[],
): Array<{
  id: string;
  file:
    | File
    | {
        // UploadedFileの場合
        url: string;
        type: string;
      };
  preview?: string;
  name: string;
  size: number;
}> => {
  return fileInfos.map((info) => {
    if (info.type === "local") {
      return {
        id: info.id,
        file: info.file,
        preview: info.preview,
        name: info.file.name,
        size: info.file.size,
      };
    }
    return {
      id: info.id,
      file: {
        url: info.fileInfo.s3Url,
        type: info.fileInfo.mimetype,
      },
      preview: info.fileInfo.s3Url,
      name: decodeURIComponent(info.fileInfo.originalname),
      size: info.fileInfo.size,
    };
  });
};
